import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Clock, Copy } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';

interface PendingProfileSubmissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  submissionType: 'initial' | 'update';
  submissionDate?: string;
}

export const PendingProfileSubmissionModal = ({ 
  isOpen, 
  onClose, 
  submissionType,
  submissionDate 
}: PendingProfileSubmissionModalProps) => {
  const { toast } = useToast();

  const handleCopyInfo = () => {
    const info = `
Tutor Profile Status: ${submissionType === 'initial' ? 'Initial Submission Pending' : 'Update Pending'}
Submitted: ${submissionDate ? new Date(submissionDate).toLocaleDateString() : 'Unknown'}
Status: Waiting for admin approval
`;
    navigator.clipboard.writeText(info.trim());
    toast({
      title: "Copied!",
      description: "Profile status information copied to clipboard.",
    });
  };

  const isInitialSubmission = submissionType === 'initial';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            {isInitialSubmission ? 'Profile Submission Pending' : 'Profile Update Pending'}
          </DialogTitle>
          <DialogDescription className="space-y-4 pt-2">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-500" />
              <Badge variant="secondary" className="bg-orange-100 text-orange-700">
                Pending Approval
              </Badge>
            </div>
            
            {isInitialSubmission ? (
              <div className="space-y-3">
                <p>
                  Your tutor profile registration is currently being reviewed by our admin team.
                </p>
                <p>
                  <strong>You cannot create a new profile while your initial submission is pending.</strong>
                </p>
                <p className="text-sm text-gray-600">
                  This process typically takes 1-2 business days. You'll receive an email notification once your profile is approved.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <p>
                  You have a profile update request currently being reviewed by our admin team.
                </p>
                <p>
                  <strong>You cannot make additional profile changes while an update is pending.</strong>
                </p>
                <p className="text-sm text-gray-600">
                  Please wait for the current update to be approved or rejected before making new changes.
                </p>
              </div>
            )}

            {submissionDate && (
              <p className="text-sm text-gray-500">
                Submitted: {new Date(submissionDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex gap-2 pt-4">
          <Button variant="outline" onClick={handleCopyInfo} className="flex-1">
            <Copy className="w-4 h-4 mr-2" />
            Copy Info
          </Button>
          <Button onClick={onClose} className="flex-1">
            Understood
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};